import { BreadCrumbCustom } from '../../components/BreadCrumbCustom/BreadCrumbCustom';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes } from 'react-router';
import { useLang } from '../../language';
import { ListadoEmpresas } from './ListadoEmpresas';
import { EmpresaDetalle } from './EmpresaDetalle';
import { ModalInstrucciones } from './ModalInstrucciones';
import { RootState } from '../../store';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

/* ------------------------------------------------------------------------------------
 * Flujo de solicitud y alta de empresa: DESACTIVADO en el módulo.
 *
 * Se ha movido a la cabecera (`components/Layouts/LayoutPrimary/Navtop.tsx`, que pinta
 * `pages/Empresas/SolicitarEmpresa`), visible desde cualquier página. Es necesario que
 * esté fuera del módulo porque `AccesoEmpresasRoute` echa a home a quien no tiene ninguna
 * empresa autorizada, y ese usuario es justo el que necesita pedir acceso.
 *
 * Se deja comentado por si en el futuro se quiere recuperar el botón dentro del módulo.
 * Para reactivarlo, lo más simple es reutilizar el componente ya extraído:
 *
 *     <SolicitarEmpresa />
 *
 * en el sitio donde estaba el botón `BUSCAR_AUTORIZAR_EMPRESA`, y volver a importarlo
 * (`import { SolicitarEmpresa } from './SolicitarEmpresa'`). No hace falta el contador
 * local: `SolicitarEmpresa` ya avisa al store.
 *
 * imports que necesitaba el bloque original:
 *   import { useEffect } from 'react';
 *   import { EmpresasServices } from './controllers/services/empresas.services';
 *   import { ModalBuscarEmpresa } from './ModalBuscarEmpresa';
 *   import { ModalStatusServer } from '../../components/ModalStatus/ModalStatusServer';
 *   import { faCheckCircle, faSearch, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
 *
 * Estado y hooks originales:
 *
 *   const [openModal, setOpenModal] = useState(false)
 *   const [refreshKey, setRefreshKey] = useState(0)
 *   const [fetchSolicitarPOST, fetchSolicitarPOSTData] = EmpresasServices.POST_SOLICITAR()
 *   const [fetchRegistrarPOST, fetchRegistrarPOSTData, resetRegistrarPOST] = EmpresasServices.POST_REGISTRAR_EMPRESA()
 *
 *   const handleAutorizar = (empresaId: number) => {
 *       fetchSolicitarPOST({ body: { empresa_id: empresaId } })
 *   }
 *
 *   const handleRegistrar = (body: any) => {
 *       fetchRegistrarPOST({ body })
 *   }
 *
 *   useEffect(() => {
 *       if (String(fetchSolicitarPOSTData.status)?.[0] === '2') {
 *           setOpenModal(false)
 *           setRefreshKey((key) => key + 1)
 *       }
 *   }, [fetchSolicitarPOSTData])
 *
 *   useEffect(() => {
 *       if (String(fetchRegistrarPOSTData.status)?.[0] === '2') {
 *           setOpenModal(false)
 *           setRefreshKey((key) => key + 1)
 *       }
 *   }, [fetchRegistrarPOSTData])
 *
 * Botón que estaba en la cabecera del listado (sustituido por `SolicitarEmpresa`):
 *
 *   <Button
 *       size="small"
 *       color="primary"
 *       variant="contained"
 *       startIcon={<FontAwesomeIcon icon={faSearch} />}
 *       onClick={() => setOpenModal(true)}
 *   >
 *       {Lang('BUSCAR_AUTORIZAR_EMPRESA')}
 *   </Button>
 *
 * Y al final del componente, los tres diálogos:
 *
 *   <ModalBuscarEmpresa
 *       open={openModal}
 *       onClose={() => setOpenModal(false)}
 *       onAutorizar={handleAutorizar}
 *       onRegistrar={handleRegistrar}
 *       registrarErrors={fetchRegistrarPOSTData.error?.errors}
 *       registrarLoading={!!fetchRegistrarPOSTData.loading}
 *   />
 *
 *   <ModalStatusServer fetchData={fetchSolicitarPOSTData} activeLoading messageStatus={{ ... }} />
 *   <ModalStatusServer fetchData={fetchRegistrarPOSTData} activeLoading onClose={resetRegistrarPOST} messageStatus={{ ... }} />
 *
 * (El contenido de los `messageStatus` está tal cual en `pages/Empresas/SolicitarEmpresa/index.tsx`.)
 * ------------------------------------------------------------------------------------ */

export const Empresas = ({ onlyPath }: any) => {

    const Lang = useLang()

    const BreadcrumbLineItems = [
        {
            title: Lang('EMPRESAS'),
            link: onlyPath
        }
    ]

    const [openInstrucciones, setOpenInstrucciones] = useState(false)

    // Lo incrementa `SolicitarEmpresa` en el store al enviar una solicitud o un alta. Va en
    // redux porque el disparador puede estar en la cabecera, que es otro árbol de React.
    const solicitudesRefreshKey = useSelector((state: RootState) => state.empresa.solicitudesRefreshKey)

    return (
        <div>
            <Routes>

                <Route path="/" element={

                    <div>
                        <BreadCrumbCustom items={BreadcrumbLineItems} />

                        <section className='LayoutPrimary__contentmain'>

                        <Box>
                            <Grid container spacing={3}>
                                <Grid item xs={12}>

                                    <Grid container spacing={2} className='mb-3' alignItems='center' justifyContent='space-between'>

                                        <Grid item xs={12} sm={8}>
                                            <Typography variant='h5' className='my-0 fw-500 color-primary'>
                                                {Lang('EMPRESAS')}
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} sm={4}>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>

                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    startIcon={<FontAwesomeIcon icon={faQuestionCircle} />}
                                                    onClick={() => setOpenInstrucciones(true)}
                                                >
                                                    {Lang('GUIA_EMPRESAS_CORTA')}
                                                </Button>

                                                {/* Desactivado: la solicitud se hace desde la cabecera.
                                                    Ver el bloque comentado de arriba. */}

                                            </Box>
                                        </Grid>

                                    </Grid>

                                    <ListadoEmpresas refreshKey={solicitudesRefreshKey} />

                                </Grid>
                            </Grid>
                        </Box>
                    </section>
                    </div>

                } />

                <Route
                    path="/:empresaId/*"
                    element={<EmpresaDetalle onlyPath='/empreses' />}
                />

            </Routes>

            <ModalInstrucciones
                open={openInstrucciones}
                onClose={() => setOpenInstrucciones(false)}
            />

        </div>
    )
}
